/**
 * FreshCards Database View Config — V5.1
 *
 * The shared brain that makes Gallery / Kanban / List / Calendar / Timeline /
 * Recent feel like 1 product. Every view consumes the same `DatabaseViewConfig`
 * and renders items through the same `useViewItems()` selector. Switching
 * views is instant (no data re-fetch) because the items list is a single
 * derived value; only the render strategy changes.
 *
 * Configs are persisted to localStorage keyed by `database.id`, so a database
 * can have multiple saved views ("Marketing Board", "My Tasks", "This Week").
 */

/*
 * ─────────────────────────────────────────────────────────────────────────
 * F 135 — DEPRECATED. THE CONTRACT NOW LIVES IN THE BRAIN.
 *
 * The view contract (filter / sort / visiblePropertyIds / propertyOrder) was
 * moved to `src/core/views/viewConfig.ts`, and persistence moved from
 * localStorage to a StorageAdapter. That module is the source of truth.
 *
 * WHAT IS STILL HERE, AND WHY
 *   - the localStorage `loadViews`/`saveViews` below are the PRE-F 135 storage
 *     path. `migrateLegacyLocalStorageViews` in core reads exactly what they
 *     write, then clears the key. They are dead once migration has run.
 *   - `selectViewItems` / `groupItems` / `getColumns` / `useViewConfig` are
 *     genuinely UI-layer: they shape rows for a render.
 *   - the type declarations below are the PRE-F 135 shape. `DatabaseViewConfig`
 *     is still what the 8 existing importers use, and keeping them is what
 *     makes this cut non-breaking.
 *
 * The declarations were NOT deleted in F 135 on purpose: removing 321 lines
 * that 8 modules import (lib.ts, ui.ts, types/models/*, KanbanView, 2 specs)
 * is a migration, not a rename, and bundling it with the new surface would
 * have made this dispatch impossible to review. `ViewConfig` from
 * `freshcards/core` is the modern name; this file is the legacy path.
 *
 * FOLLOW-UP: once `useViewConfig` and the localStorage helpers are ported to
 * the adapter, delete this file and repoint the 8 importers.
 * ─────────────────────────────────────────────────────────────────────────
 */

import type { DatabaseItem } from '../../types/models/Database';
import { groupRecords } from '../../../core/views/groupRecords';

// ─── View types ─────────────────────────────────────────────────────────────

export type ViewType =
  | 'gallery'
  | 'kanban'
  | 'list'
  | 'calendar'
  | 'timeline'
  | 'recent';

export type SortKey = 'created' | 'updated' | 'title' | 'date' | 'priority' | 'manual';
export type SortDir = 'asc' | 'desc';

/**
 * F 504 — THE DATE MODES, added to the CORE union.
 *
 * ⇒⇒⇒⇒⇒⇒⇒⇒ MEASURED 2026-10-31: `grep -rn "granularity|byDay|byMonth|
 * Uncategorized" src/` returned ZERO. ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒⇒ EVERY mode above is
 * PROPERTY-SHAPED, so "group by Created Date" had NO mode. ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒⇒
 * ⇒⇒⇒ ⇒⇒⇒⇒ The canonical union is `src/core/views/viewConfig.ts`; this
 * one is the UI twin and it CARRIES THE SAME MEMBERS, or the two drift.
 *
 * ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒⇒ **⇒⇒⇒ A GRANULARITY MAY NOT RESTATE A MODE.**
 * ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒⇒ The dispatch also named 'status' as a granularity —
 * but `groupBy: 'status'` already IS that, so a second spelling would be
 * the 3-SOURCE DRIFT this file's own header records removing.
 */
export type GroupByMode =
  | 'status'        // 5-col Status board (Backlog / In Progress / Review / Done / Archived)
  | 'priority'      // 4-col Priority (Urgent / High / Medium / Low)
  | 'assignee'      // 1 col per person
  | 'tag'           // 1 col per tag
  | 'due-week'      // This week / Next week / Later / Overdue
  | 'none'          // single flat column with drag-to-reorder
  | 'created-day'   // F 504 — one column per calendar day, newest first
  | 'created-month' // F 504 — one column per calendar month, newest first
  // F 509 — THE GENERAL DATE MODE. The PROPERTY is named by `groupByField`
  // and the BUCKET SIZE by `groupGranularity`. One mode, not one per
  // property — a mode per property would be unbounded and would break the
  // exhaustive `GROUP_BY_LABELS` map in `ViewSwitcher.tsx`.
  | 'date'

export interface DatabaseViewConfig {
  id: string;                  // unique per view, e.g. 'view_marketing_board'
  databaseId: string;          // parent database
  type: ViewType;
  /**
   * F 280 — the F 135+ name for the same choice as `type`.
   *
   * `type` is the pre-F 135 union and has SIX members. F 136 added four
   * layouts (`card`/`table`/`dense`/`detail`) that `ViewType` cannot express, so
   * before this field a table view had nowhere legal to record itself. F 135
   * already taught `coerceViewConfig` to read `layout` first and `type` as
   * fallback, so writing the F 136 four here needs no reader change.
   *
   * Both are kept. `type` stays populated for the six legacy layouts so the
   * 8 existing importers that read it keep working; `layout` is authoritative
   * wherever it is present.
   */
  layout?: import('../../../core/views/viewConfig').ViewLayout;
  name: string;                // user-visible label
  /**
   * F 280 — THE FILTER IS PER-VIEW, NOT PER-SURFACE.
   *
   * Until F 280 this lived in `useState` on `DatabaseView` with the comment
   * "applies to every view", so switching views carried the filter along and a
   * reload threw it away. It is here now, beside `sortBy`/`sortDir`, and
   * therefore persisted by `saveViews` with everything else.
   *
   * DELIBERATELY `PropertyFilter`, NOT core's `FilterRule[]`. The two are not
   * interchangeable: `PropertyFilter` is a grouped OR ("status is one of
   * these"), while `applyViewRules` in core is a flat AND over every rule, and
   * `FilterOperator` has no `is-any-of`. Flattening this into `FilterRule[]`
   * would demand status==='a' AND status==='b' and silently return nothing.
   * The unification is real work with its own blast radius; see F 280 Item 4.
   */
  filter?: import('./calendarConfig').PropertyFilter;
  groupBy?: GroupByMode;       // for kanban
  /**
   * F 504 — WHICH FIELD TO GROUP BY. `groupBy` says HOW; this says WHAT BY.
   * Lives on the VIEW, never on the database record — see the core twin's
   * `groupByField` for the full reasoning.
   */
  groupByField?: string;
  /**
   * F 509 — HOW COARSELY TO BUCKET THE DATE.
   *
   * ⇒⇒⇒⇒⇒⇒⇒⇒ MEASURED 2026-11-01: F 504's COMMENT IS EXPLICIT — "A
   * GRANULARITY MAY NOT RESTATE A MODE." ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒⇒ `created-day`
   * RESTATES `groupBy`, AND THE F 506 SHIP LEFT IT THAT WAY. ⇒⇒⇒⇒⇒⇒⇒⇒
   * ⇒⇒⇒ ⇒⇒⇒⇒ **⇒⇒⇒ THE FIX IS A REAL, INDEPENDENT AXIS** — `groupBy` names
   * ⇒⇒⇒ ⇒⇒⇒⇒ the FAMILY, `groupByField` names the PROPERTY, AND THIS names
   * ⇒⇒⇒ ⇒⇒⇒⇒ the BUCKET SIZE.
   *
   * ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒⇒ **⇒⇒□ THE LEGACY MODES KEEP THEIR OWN IMPLIED
   * GRANULARITY** (`created-day` → day, `created-month` → month) SO A SAVED
   * ⇒⇒⇒ ⇒⇒⇒⇒ ⇒⇒□ VIEW RENDERS EXACTLY AS IT DID — AND THE `date` MODE
   * ⇒⇒⇒ ⇒⇒⇒⇒ ⇒⇒□ READS THIS FIELD.
   *
   * ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒□ ⇒⇒□ **⇒⇒□ THE VALUES ARE NOTION'S** — RESEARCHED
   * 2026-11-01 FROM NOTION'S OWN API DOCS: `"group_by": "relative" | "day" |
   * ⇒⇒□ ⇒⇒□ "week" | "month" | "year"`.
   */
  groupGranularity?: import('../../../core/views/groupRecords').GroupGranularity;
  /**
   * F 506 — THE BOARD'S PRESENTATION TOGGLES.
   *
   * ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THESE LIVE ON THE VIEW, BESIDE
   * `groupBy`, BECAUSE THEY ARE A PROPERTY OF THE VIEW AND NOT OF THE
   * APP.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ ONE BOARD WRAPS ITS CARDS AND
   * ANOTHER DOES NOT; A GLOBAL TOGGLE WOULD MAKE THAT IMPOSSIBLE TO
   * EXPRESS.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□
   *
   * ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒□ ⇒⇒□ ⇒⇒□ AND THEY ARE PERSISTED WITH EVERYTHING
   * ELSE, BECAUSE THE PUSH/PULL TEST IS ABOUT A RELOAD. ⇒⇒□ ⇒⇒□ ⇒⇒□
   * ⇒⇒□ ⇒⇒□ **⇒⇒□ A TOGGLE THAT RESETS ON RELOAD IS NOT A SETTING;
   * IT IS A MOOD.**
   *
   * ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒□ ⇒⇒□ ⇒⇒□ DEFAULTS ARE STATED HERE, NOT AT THE READ
   * SITE, BECAUSE THE READ SITE IS THE UI AND THE WRITE SITE IS THE
   * GRAPH AND THE TWO MUST AGREE. ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□
   * `wrapContent` DEFAULTS FALSE (clamped), `colorColumns` DEFAULTS TRUE
   * (a board without colour is a list of grey boxes), `showIcons`
   * DEFAULTS TRUE.**
   */
  wrapContent?: boolean;
  colorColumns?: boolean;
  showIcons?: boolean;
  sortBy?: SortKey;            // for list / recent / gallery
  sortDir?: SortDir;
  coverPropId?: string;        // for gallery
  datePropId?: string;         // for calendar / timeline
  titlePropId?: string;        // for list inline-edit
  createdAt: number;
  updatedAt: number;
}

// ─── Default views per database ─────────────────────────────────────────────

/**
 * The 6 default views a new database gets. The user can delete any, rename
 * any, and create more.
 */
export const DEFAULT_VIEW_CONFIGS: Omit<DatabaseViewConfig, 'id' | 'databaseId' | 'createdAt' | 'updatedAt'>[] = [
  { type: 'gallery',  name: 'Gallery',  coverPropId: 'cover',  sortBy: 'updated', sortDir: 'desc' },
  { type: 'kanban',   name: 'Board',    groupBy: 'status' },
  { type: 'list',     name: 'List',     sortBy: 'updated', sortDir: 'desc' },
  { type: 'calendar', name: 'Calendar', datePropId: 'date' },
  { type: 'timeline', name: 'Timeline', datePropId: 'date' },
  { type: 'recent',   name: 'Recent',   sortBy: 'updated', sortDir: 'desc' },
];

// ─── localStorage persistence ────────────────────────────────────────────────

const STORAGE_PREFIX = 'freshcards:views:';

function key(databaseId: string): string {
  return `${STORAGE_PREFIX}${databaseId}`;
}

export function loadViews(databaseId: string): DatabaseViewConfig[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(key(databaseId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed as DatabaseViewConfig[];
  } catch {
    return [];
  }
}

export function saveViews(databaseId: string, views: DatabaseViewConfig[]): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(key(databaseId), JSON.stringify(views));
  } catch (e) {
    console.error('[viewConfig] failed to persist views', e);
  }
}

/**
 * Get-or-create the default view set for a database. If the database has no
 * stored views, the 6 defaults are minted. The first view is the default
 * active view.
 */
export function getOrCreateDefaultViews(databaseId: string): DatabaseViewConfig[] {
  const existing = loadViews(databaseId);
  if (existing.length > 0) return existing;
  const now = Date.now();
  const minted: DatabaseViewConfig[] = DEFAULT_VIEW_CONFIGS.map((v, i) => ({
    ...v,
    id: `view_${databaseId}_${v.type}`,
    databaseId,
    createdAt: now,
    updatedAt: now,
  }));
  saveViews(databaseId, minted);
  return minted;
}

// ─── Item selectors (the single source of truth for "what shows in this view") ─

/**
 * Resolve the sort key on a `DatabaseItem` to a comparable primitive. Returns
 * `null` for missing values so the comparator can bucket them at the end.
 */
function sortValue(item: DatabaseItem, key: SortKey, datePropId?: string): string | number | null {
  switch (key) {
    case 'title':
      return item.title.toLowerCase();
    case 'created':
      return item.createdAt ? new Date(item.createdAt).getTime() : null;
    case 'updated':
      return item.updatedAt ? new Date(item.updatedAt).getTime() : null;
    case 'date': {
      if (!datePropId) return null;
      const p = item.properties.find(p => p.propertyId === datePropId);
      if (!p?.value) return null;
      const v = String(p.value);
      const t = new Date(v.split('T')[0]).getTime();
      return Number.isNaN(t) ? null : t;
    }
    case 'priority': {
      // lower number = higher priority
      const p = item.properties.find(p => p.propertyId === 'priority');
      const v = p?.value as { rank?: number } | undefined;
      return v?.rank ?? null;
    }
    case 'manual':
      return null;
  }
}

export interface SelectorInput {
  items: DatabaseItem[];
  config: DatabaseViewConfig;
}

export interface SelectorOutput {
  items: DatabaseItem[];           // sorted
  itemsByColumn?: Map<string, DatabaseItem[]>; // for kanban / groupBy views
  totalCount: number;
}

/**
 * Pure selector. No side effects. Given items + a config, returns the items
 * shaped for that view (already sorted, optionally grouped).
 */
export function selectViewItems({ items, config }: SelectorInput): SelectorOutput {
  const total = items.length;
  const sortBy = config.sortBy ?? 'updated';
  const sortDir = config.sortDir ?? 'desc';
  const datePropId = config.datePropId;

  // 1. Sort
  const sorted = [...items].sort((a, b) => {
    const av = sortValue(a, sortBy, datePropId);
    const bv = sortValue(b, sortBy, datePropId);
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  // 2. Group (for kanban / timeline)
  if (config.type === 'kanban' && config.groupBy && config.groupBy !== 'none') {
    const itemsByColumn = groupItems(sorted, config.groupBy);
    return { items: sorted, itemsByColumn, totalCount: total };
  }

  return { items: sorted, totalCount: total };
}

// ─── Group-by logic (used by Kanban) ─────────────────────────────────────────

/**
 * Normalise a multi-select property value to a list of stable string keys.
 *
 * F 367: this value is `PropertyValue.value`, typed `unknown`, and the two
 * shapes that legitimately occur in FCC are BOTH in the wild:
 *   - `string[]`                  — what the seeded fixtures and KanbanView's
 *                                   drag handler produce
 *   - `SelectOption[]` ({id,name}) — what every real `multi_select` adapter
 *                                   produces (PropertyChip, coverResolver,
 *                                   the Notion/FCC database import)
 *
 * Interpolating an object into a key yields `tag_[object Object]`, which never
 * matches a registered column, so `groups.get(...)` returns `undefined` and the
 * push throws — a full white screen (measured: rootChildren 0, bodyChars 0).
 *
 * Keys are the option `id` when present (stable across renames), else `name`,
 * else the raw string. Display names come from the same pass, so the column
 * heading stays human-readable even when the key is an opaque id.
 */
export interface TagKey {
  /** Stable key used for the column id — never `[object Object]`. */
  key: string;
  /** Human-readable label for the column heading. */
  name: string;
}

export function normaliseTagKeys(value: unknown): TagKey[] {
  if (value == null) return [];
  const raw = Array.isArray(value) ? value : [value];
  const out: TagKey[] = [];
  const seen = new Set<string>();
  for (const entry of raw) {
    let key: string | undefined;
    let name: string | undefined;
    if (typeof entry === 'string') {
      key = entry;
      name = entry;
    } else if (entry && typeof entry === 'object') {
      const o = entry as { id?: unknown; name?: unknown; value?: unknown; label?: unknown };
      const idPart = o.id ?? o.value;
      if (typeof idPart === 'string' && idPart) key = idPart;
      const namePart = o.name ?? o.label ?? o.value;
      if (typeof namePart === 'string' && namePart) name = namePart;
    } else if (entry != null) {
      key = String(entry);
      name = key;
    }
    // A `{name}`-only option has no id: the name IS the key.
    if (!key && name) key = name;
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push({ key, name: name || key });
  }
  return out;
}

/**
 * Push an item into a group column, creating the array if it is absent.
 *
 * F 367: every branch of groupItems used `groups.get(id)!.push(item)`. A
 * non-null assertion on a Map lookup is only as safe as the invariant that the
 * key was registered first — and every real crash here came from a key that was
 * NOT. Creating on demand makes a missing column degrade to a new column
 * instead of throwing away the whole surface.
 */
function pushToGroup(
  groups: Map<string, DatabaseItem[]>,
  colId: string,
  item: DatabaseItem,
): void {
  const col = groups.get(colId);
  if (col) col.push(item);
  else groups.set(colId, [item]);
}

export function groupItems(items: DatabaseItem[], mode: GroupByMode): Map<string, DatabaseItem[]> {
  const groups = new Map<string, DatabaseItem[]>();

  if (mode === 'status') {
    const cols = [
      { id: 'status_backlog',     name: 'Backlog',     color: '#94a3b8' },
      { id: 'status_in_progress', name: 'In Progress', color: '#3b82f6' },
      { id: 'status_review',      name: 'Review',      color: '#a855f7' },
      { id: 'status_done',        name: 'Done',        color: '#22c55e' },
      { id: 'status_archived',    name: 'Archived',    color: '#475569' },
    ];
    cols.forEach(c => groups.set(c.id, []));
    items.forEach(item => {
      const p = item.properties.find(p => p.propertyId === 'status');
      const statusId = (p?.value as { id?: string } | undefined)?.id;
      const col = cols.find(c => c.id === statusId) ?? cols[0];
      pushToGroup(groups, col.id, item);
    });
    // Attach column metadata via a hidden sentinel so consumers can iterate
    (groups as any)._columns = cols;
    return groups;
  }

  if (mode === 'priority') {
    const cols = [
      { id: 'prio_urgent', name: '🔥 Urgent', color: '#ef4444' },
      { id: 'prio_high',   name: '⚡ High',   color: '#f97316' },
      { id: 'prio_medium', name: '◐ Medium', color: '#eab308' },
      { id: 'prio_low',    name: '✓ Low',    color: '#22c55e' },
    ];
    cols.forEach(c => groups.set(c.id, []));
    items.forEach(item => {
      const p = item.properties.find(p => p.propertyId === 'priority');
      const prioId = (p?.value as { id?: string } | undefined)?.id ?? 'prio_low';
      const col = cols.find(c => c.id === prioId) ?? cols[cols.length - 1];
      pushToGroup(groups, col.id, item);
    });
    (groups as any)._columns = cols;
    return groups;
  }

  if (mode === 'assignee') {
    const people = new Map<string, { name: string }>();
    items.forEach(item => {
      const p = item.properties.find(p => p.propertyId === 'assignee');
      const a = p?.value as { id?: string; name?: string } | undefined;
      if (a?.id) people.set(a.id, { name: a.name ?? a.id });
    });
    const cols = Array.from(people.entries()).map(([id, p]) => ({ id: `asg_${id}`, name: p.name, color: '#0ea5e9' }));
    if (cols.length === 0) cols.push({ id: 'asg_unassigned', name: 'Unassigned', color: '#94a3b8' });
    cols.forEach(c => groups.set(c.id, []));
    items.forEach(item => {
      const p = item.properties.find(p => p.propertyId === 'assignee');
      const a = p?.value as { id?: string } | undefined;
      const colId = a?.id ? `asg_${a.id}` : 'asg_unassigned';
      pushToGroup(groups, colId, item);
    });
    (groups as any)._columns = cols;
    return groups;
  }

  if (mode === 'tag') {
    const tags = new Map<string, TagKey>();
    items.forEach(item => {
      const p = item.properties.find(p => p.propertyId === 'tag');
      normaliseTagKeys(p?.value).forEach(t => tags.set(t.key, t));
    });
    const cols = Array.from(tags.values()).map(t => ({ id: `tag_${t.key}`, name: t.name, color: '#a855f7' }));
    if (cols.length === 0) cols.push({ id: 'tag_untagged', name: 'Untagged', color: '#94a3b8' });
    cols.forEach(c => groups.set(c.id, []));
    items.forEach(item => {
      const p = item.properties.find(p => p.propertyId === 'tag');
      const list = normaliseTagKeys(p?.value);
      if (list.length === 0) {
        // A missing column must degrade to Untagged, never to an exception that
        // takes the whole surface down — so create on demand instead of `!`.
        pushToGroup(groups, 'tag_untagged', item);
      } else {
        list.forEach(t => {
          // A key that never made it into `cols` (added between the two
          // passes) still deserves a column rather than a crash.
          pushToGroup(groups, `tag_${t.key}`, item);
        });
      }
    });
    (groups as any)._columns = cols;
    return groups;
  }

  if (mode === 'due-week') {
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay());
    startOfWeek.setHours(0, 0, 0, 0);
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 7);
    const nextWeekEnd = new Date(endOfWeek);
    nextWeekEnd.setDate(endOfWeek.getDate() + 7);
    const cols = [
      { id: 'due_overdue',  name: '⚠ Overdue',  color: '#ef4444' },
      { id: 'due_thisweek', name: 'This Week',  color: '#3b82f6' },
      { id: 'due_nextweek', name: 'Next Week',  color: '#a855f7' },
      { id: 'due_later',    name: 'Later',      color: '#94a3b8' },
    ];
    cols.forEach(c => groups.set(c.id, []));
    items.forEach(item => {
      const p = item.properties.find(p => p.propertyId === 'date');
      if (!p?.value) {
        pushToGroup(groups, 'due_later', item);
        return;
      }
      const d = new Date(String(p.value).split('T')[0]);
      if (d < startOfWeek) pushToGroup(groups, 'due_overdue', item);
      else if (d < endOfWeek) pushToGroup(groups, 'due_thisweek', item);
      else if (d < nextWeekEnd) pushToGroup(groups, 'due_nextweek', item);
      else pushToGroup(groups, 'due_later', item);
    });
    (groups as any)._columns = cols;
    return groups;
  }

  return groups;
}

// ─── Column metadata accessor ────────────────────────────────────────────────

/**
 * Pull the column metadata (id, name, color) off a grouped Map. Kanban
 * reads this to render its headers.
 */
export function getColumns(groups: Map<string, DatabaseItem[]>): Array<{ id: string; name: string; color: string }> {
  return (groups as any)._columns ?? [];
}
